import axios from "axios";

import { updateLocalStorage } from "../utils/Helpers";
import {
  getApiUrl,
  getAccessToken,
  isAccessTokenExpired,
  maybeRemoveTrailingSlash,
} from "../utils/api";
import { refreshLogin, shouldRememberLogin } from "./auth";

/**
 * Create the api axios instances.
 */
export const api = axios.create({
  baseURL: getApiUrl(),
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Update baseURL before each request.
 * Inject access token to request made with api instance.
 */
api.interceptors.request.use(
  (config) => {
    config.baseURL = getApiUrl();

    const token = config.token || getAccessToken();

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Refresh access token if it's been expired on a failed api response.
 */
api.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    const originalConfig = err.config;

    if (err.response) {
      if (
        shouldRememberLogin() &&
        isAccessTokenExpired(err.response.status) &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;

        try {
          const data = await refreshLogin();

          updateLocalStorage(data, "user");

          api.defaults.headers.common["Authorization"] = data.token;

          return api(originalConfig);
        } catch (_error) {
          if (_error.response && _error.response.data) {
            return Promise.reject(_error.response.data);
          }

          return Promise.reject(_error);
        }
      }

      if (err.response.status === 403 && err.response.data) {
        return Promise.reject(err.response.data);
      }
    }

    return Promise.reject(err);
  }
);

/**
 * Maybe remove trailing slash before each request.
 */
api.interceptors.request.use(maybeRemoveTrailingSlash);
